import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as signalR from '@microsoft/signalr';
import { useConfig } from '../providers/config-provider';
import { useAuth } from '../providers/auth-provider';
import { useApiClient, buildRestaurantPath } from '../lib/api';
import SessionStateScreen from '../components/auth/SessionStateScreen';
import { readPanelScope } from '../utils/panelScopeStorage';
import {
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
  SpeakerWaveIcon,
  SpeakerXMarkIcon,
  ArrowLeftOnRectangleIcon,
  SignalIcon,
  SignalSlashIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';

/* ─── constants ─── */

const MAX_HISTORY = 8;

const normalizeBase = (value) => (value || '').replace(/\/+$/, '');

const formatTime = (dateValue) => {
  if (!dateValue) return '--:--';
  const d = new Date(dateValue);
  return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
};

const formatElapsed = (dateValue, referenceMs = Date.now()) => {
  if (!dateValue) return '';
  const diff = Math.max(0, Math.floor((referenceMs - new Date(dateValue).getTime()) / 1000));
  if (diff < 60) return `${diff}s`;
  const minutes = Math.floor(diff / 60);
  if (minutes < 60) return `${minutes}min`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}min`;
};

/* ─── component ─── */

export default function PanelDisplayPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { apiBase, restaurantId, setOperationalScope } = useConfig();
  const api = useApiClient();

  const [calls, setCalls] = useState([]);
  const [connectionState, setConnectionState] = useState('disconnected');
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [nowTick, setNowTick] = useState(Date.now());
  const [restaurantName, setRestaurantName] = useState('');
  const [companyName, setCompanyName] = useState('');

  const connectionRef = useRef(null);
  const audioContextRef = useRef(null);
  const audioLastPlayedRef = useRef(0);
  const wakeLockRef = useRef(null);
  const tickIntervalRef = useRef(null);
  const scopeRestoreAttemptedRef = useRef(false);
  const [scopeStatus, setScopeStatus] = useState(() =>
    restaurantId ? 'ready' : 'restoring',
  );

  /* ─── read scope ─── */

  useEffect(() => {
    const scope = readPanelScope();
    if (scope?.restaurantName) setRestaurantName(scope.restaurantName);
    if (scope?.companyName) setCompanyName(scope.companyName);
  }, []);

  useEffect(() => {
    document.title = restaurantName
      ? `Painel — ${restaurantName} | Orion WaiterCall`
      : 'Painel de Chamadas | Orion WaiterCall';
  }, [restaurantName]);

  /* ─── access guard ─── */

  useEffect(() => {
    if (restaurantId) {
      setScopeStatus('ready');
      return;
    }

    if (scopeRestoreAttemptedRef.current) {
      setScopeStatus('missing');
      return;
    }

    const storedScope = readPanelScope();
    if (!storedScope?.companyId || !storedScope?.restaurantId) {
      setScopeStatus('missing');
      return;
    }

    scopeRestoreAttemptedRef.current = true;
    if (storedScope.restaurantName) {
      setRestaurantName(storedScope.restaurantName);
    }
    if (storedScope.companyName) {
      setCompanyName(storedScope.companyName);
    }

    setScopeStatus('restoring');
    setOperationalScope(storedScope.companyId, storedScope.restaurantId);
  }, [restaurantId, setOperationalScope]);

  useEffect(() => {
    if (scopeStatus === 'missing') {
      navigate('/painel/login', { replace: true });
    }
  }, [navigate, scopeStatus]);

  /* ─── live clock ─── */

  useEffect(() => {
    tickIntervalRef.current = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(tickIntervalRef.current);
  }, []);

  /* ─── wake lock ─── */

  useEffect(() => {
    const requestWakeLock = async () => {
      try {
        if ('wakeLock' in navigator) {
          wakeLockRef.current = await navigator.wakeLock.request('screen');
        }
      } catch { /* not supported or denied */ }
    };
    requestWakeLock();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') requestWakeLock();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      wakeLockRef.current?.release().catch(() => {});
    };
  }, []);

  /* ─── fullscreen ─── */

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {});
    } else {
      document.exitFullscreen().catch(() => {});
    }
  }, []);

  useEffect(() => {
    const handler = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  /* ─── sound ─── */

  const playAlertSound = useCallback(() => {
    if (!soundEnabled) return;
    const now = Date.now();
    if (now - audioLastPlayedRef.current < 1200) return;
    audioLastPlayedRef.current = now;

    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return;

    if (!audioContextRef.current) {
      audioContextRef.current = new AudioContextClass();
    }
    const ctx = audioContextRef.current;
    if (ctx.state === 'suspended') ctx.resume().catch(() => {});

    const startAt = ctx.currentTime;
    const tones = [
      { freq: 660, dur: 0.15, delay: 0 },
      { freq: 880, dur: 0.15, delay: 0.2 },
      { freq: 1100, dur: 0.2, delay: 0.4 },
    ];

    tones.forEach(({ freq, dur, delay }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.25, startAt + delay);
      gain.gain.exponentialRampToValueAtTime(0.001, startAt + delay + dur);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(startAt + delay);
      osc.stop(startAt + delay + dur + 0.05);
    });
  }, [soundEnabled]);

  /* ─── SignalR event handler ─── */

  const handleRealtime = useCallback(
    (call) => {
      if (!call?.id) return;

      setCalls((prev) => {
        const without = prev.filter((c) => c.id !== call.id);

        if (call.status === 'Open') {
          playAlertSound();
          return [call, ...without];
        }

        if (call.status === 'Claimed') {
          return [call, ...without];
        }

        /* Resolved / Canceled / Expired → remove from panel */
        return without;
      });
    },
    [playAlertSound]
  );

  /* ─── load initial calls ─── */

  const loadInitialCalls = useCallback(async () => {
    if (!restaurantId) return;
    try {
      const res = await api.get(buildRestaurantPath(restaurantId, 'calls'), {
        params: { page: 1, pageSize: 30 },
      });
      const items = res.data?.items ?? res.data ?? [];
      const active = items
        .filter((c) => c.status === 'Open' || c.status === 'Claimed')
        .sort((a, b) => new Date(b.openedAt).getTime() - new Date(a.openedAt).getTime());
      setCalls(active);
    } catch {
      /* will retry on reconnect */
    }
  }, [api, restaurantId]);

  /* ─── SignalR connection ─── */

  const connectSignalR = useCallback(async () => {
    if (!restaurantId || !apiBase) return;
    if (connectionRef.current) return;

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${normalizeBase(apiBase)}/hubs/calls`, { withCredentials: true })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 20000, 30000])
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    connection.on('call.created', handleRealtime);
    connection.on('call.claimed', handleRealtime);
    connection.on('call.resolved', handleRealtime);
    connection.on('call.recalled', handleRealtime);
    connection.on('call.escalated', handleRealtime);
    connection.on('call.reopened', handleRealtime);

    connection.onreconnecting(() => setConnectionState('reconnecting'));
    connection.onreconnected(async () => {
      setConnectionState('connected');
      try {
        await connection.invoke('JoinRestaurantGroup', restaurantId);
      } catch { /* ignore */ }
      loadInitialCalls();
    });
    connection.onclose(() => setConnectionState('disconnected'));

    connectionRef.current = connection;

    try {
      await connection.start();
      setConnectionState('connected');
      await connection.invoke('JoinRestaurantGroup', restaurantId);
      await loadInitialCalls();
    } catch {
      setConnectionState('disconnected');
      connectionRef.current = null;
    }
  }, [restaurantId, apiBase, handleRealtime, loadInitialCalls]);

  const disconnectSignalR = useCallback(async () => {
    const conn = connectionRef.current;
    if (!conn) return;
    connectionRef.current = null;
    try {
      if (restaurantId) {
        await conn.invoke('LeaveRestaurantGroup', restaurantId);
      }
    } catch { /* ignore */ }
    await conn.stop();
    setConnectionState('disconnected');
  }, [restaurantId]);

  useEffect(() => {
    connectSignalR();
    return () => { disconnectSignalR(); };
  }, [connectSignalR, disconnectSignalR]);

  /* ─── derived state ─── */

  const latestCall = calls[0] ?? null;
  const history = calls.slice(1, MAX_HISTORY + 1);
  const pendingCount = calls.filter((c) => c.status === 'Open').length;

  const currentTime = new Date(nowTick).toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const currentDate = new Date(nowTick).toLocaleDateString('pt-BR', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
  });

  /* ─── handle exit ─── */

  const handleExit = () => {
    disconnectSignalR();
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    }
    logout();
    navigate('/painel/login', { replace: true });
  };

  /* ─── render ─── */

  if (scopeStatus !== 'ready') {
    return (
      <SessionStateScreen
        variant="loading"
        title="Preparando exibição do painel"
        message="Estamos restaurando o escopo operacional antes de conectar o painel em tempo real."
      />
    );
  }

  return (
    <div className="flex h-screen w-screen flex-col overflow-hidden bg-gray-950 text-white select-none">
      {/* ─── top bar ─── */}
      <header className="flex shrink-0 items-center justify-between border-b border-white/10 bg-gray-900/80 px-6 py-3 backdrop-blur-sm">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div
              className={clsx(
                'h-2.5 w-2.5 rounded-full',
                connectionState === 'connected' && 'bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.5)]',
                connectionState === 'reconnecting' && 'bg-amber-400 animate-pulse',
                connectionState === 'disconnected' && 'bg-red-400'
              )}
            />
            {connectionState === 'connected' ? (
              <SignalIcon className="h-4 w-4 text-emerald-400" />
            ) : (
              <SignalSlashIcon className="h-4 w-4 text-red-400" />
            )}
          </div>
          <div className="h-5 w-px bg-white/10" />
          <h1 className="text-base font-semibold tracking-wide text-white/90">
            {restaurantName || 'Painel de Chamadas'}
          </h1>
          {companyName && (
            <>
              <span className="hidden sm:block h-5 w-px bg-white/10" />
              <span className="hidden sm:block text-sm text-white/40">{companyName}</span>
            </>
          )}
        </div>

        <div className="flex items-center gap-1">
          {pendingCount > 0 && (
            <span className="mr-3 inline-flex items-center gap-1.5 rounded-full bg-indigo-500/20 px-3 py-1 text-xs font-semibold text-indigo-300 ring-1 ring-inset ring-indigo-500/30">
              <span className="h-1.5 w-1.5 rounded-full bg-indigo-400 animate-pulse" />
              {pendingCount} pendente{pendingCount !== 1 ? 's' : ''}
            </span>
          )}
          <button
            onClick={() => setSoundEnabled((prev) => !prev)}
            className="rounded-lg p-2 text-white/50 transition hover:bg-white/10 hover:text-white/80"
            title={soundEnabled ? 'Desativar som' : 'Ativar som'}
          >
            {soundEnabled ? (
              <SpeakerWaveIcon className="h-5 w-5" />
            ) : (
              <SpeakerXMarkIcon className="h-5 w-5" />
            )}
          </button>
          <button
            onClick={toggleFullscreen}
            className="rounded-lg p-2 text-white/50 transition hover:bg-white/10 hover:text-white/80"
            title={isFullscreen ? 'Sair da tela cheia' : 'Tela cheia'}
          >
            {isFullscreen ? (
              <ArrowsPointingInIcon className="h-5 w-5" />
            ) : (
              <ArrowsPointingOutIcon className="h-5 w-5" />
            )}
          </button>
          <button
            onClick={handleExit}
            className="rounded-lg p-2 text-white/50 transition hover:bg-white/10 hover:text-red-400"
            title="Sair do painel"
          >
            <ArrowLeftOnRectangleIcon className="h-5 w-5" />
          </button>
        </div>
      </header>

      {/* ─── main content ─── */}
      <main className="flex flex-1 overflow-hidden">
        {/* ─── latest call (center/left) ─── */}
        <div className="flex flex-1 items-center justify-center px-8">
          {latestCall ? (
            <div
              key={latestCall.id}
              className={clsx(
                'flex flex-col items-center gap-6 animate-in fade-in duration-500',
                latestCall.status === 'Open' && 'animate-pulse-subtle'
              )}
            >
              <p
                className={clsx(
                  'text-lg font-medium uppercase tracking-widest',
                  latestCall.status === 'Open' && latestCall.escalatedAt
                    ? 'text-rose-400'
                    : latestCall.status === 'Open'
                      ? 'text-indigo-400'
                      : 'text-emerald-400'
                )}
              >
                {latestCall.status === 'Open' && latestCall.escalatedAt
                  ? 'Chamada Escalada'
                  : latestCall.status === 'Open'
                    ? 'Chamada Ativa'
                    : 'Em Atendimento'}
              </p>

              <div className="relative">
                <div
                  className={clsx(
                    'absolute -inset-8 rounded-3xl opacity-20 blur-2xl',
                    latestCall.status === 'Open' && latestCall.escalatedAt
                      ? 'bg-rose-500'
                      : latestCall.status === 'Open'
                        ? 'bg-indigo-500'
                        : 'bg-emerald-500'
                  )}
                />
                <div
                  className={clsx(
                    'relative flex h-64 w-64 items-center justify-center rounded-3xl border-2 sm:h-72 sm:w-72 md:h-80 md:w-80 lg:h-96 lg:w-96',
                    latestCall.status === 'Open' && latestCall.escalatedAt
                      ? 'border-rose-500/50 bg-rose-500/10'
                      : latestCall.status === 'Open'
                        ? 'border-indigo-500/50 bg-indigo-500/10'
                        : 'border-emerald-500/50 bg-emerald-500/10'
                  )}
                >
                  <span
                    className={clsx(
                      'text-center font-black leading-none tracking-tighter',
                      (latestCall.tableNumberLabel?.length ?? 0) > 3
                        ? 'text-7xl sm:text-8xl md:text-9xl'
                        : 'text-8xl sm:text-9xl md:text-[10rem] lg:text-[12rem]'
                    )}
                  >
                    {latestCall.tableNumberLabel ?? '—'}
                  </span>
                </div>
              </div>

              {latestCall.tableArea && (
                <p className="text-xl font-medium text-white/60">{latestCall.tableArea}</p>
              )}

              <div className="flex items-center gap-4 text-sm text-white/40">
                <span>Aberta às {formatTime(latestCall.openedAt)}</span>
                <span className="h-1 w-1 rounded-full bg-white/20" />
                <span>{formatElapsed(latestCall.openedAt, nowTick)} atrás</span>
                {latestCall.recallCount > 0 && (
                  <>
                    <span className="h-1 w-1 rounded-full bg-white/20" />
                    <span className="text-amber-400">
                      Re-chamada {latestCall.recallCount}×
                    </span>
                  </>
                )}
              </div>

              {latestCall.claimedByUserName && (
                <p className="text-sm font-medium text-emerald-400/80">
                  Atendente: {latestCall.claimedByUserName}
                </p>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center gap-4 text-center">
              <div className="flex h-24 w-24 items-center justify-center rounded-2xl bg-white/5">
                <svg className="h-12 w-12 text-white/20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0"
                  />
                </svg>
              </div>
              <p className="text-2xl font-semibold text-white/30">Nenhuma chamada no momento</p>
              <p className="text-sm text-white/15">As chamadas aparecerão automaticamente aqui</p>
            </div>
          )}
        </div>

        {/* ─── history sidebar ─── */}
        <aside className="hidden w-80 shrink-0 flex-col border-l border-white/10 bg-gray-900/50 xl:w-96 lg:flex">
          <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-white/50">
              Chamadas Anteriores
            </h2>
            <span className="text-xs text-white/30">{history.length} itens</span>
          </div>

          <div className="flex-1 overflow-y-auto">
            {history.length === 0 ? (
              <div className="flex h-full items-center justify-center p-6">
                <p className="text-sm text-white/20">Sem chamadas anteriores</p>
              </div>
            ) : (
              <ul className="divide-y divide-white/5">
                {history.map((call, index) => (
                  <li
                    key={call.id}
                    className={clsx(
                      'flex items-center gap-4 px-5 py-4 transition-colors',
                      index === 0 && 'bg-white/[0.03]'
                    )}
                  >
                    <div
                      className={clsx(
                        'flex h-14 w-14 shrink-0 items-center justify-center rounded-xl text-xl font-bold',
                        call.status === 'Open' && call.escalatedAt
                          ? 'bg-rose-500/15 text-rose-400 ring-1 ring-rose-500/30'
                          : call.status === 'Open'
                            ? 'bg-indigo-500/15 text-indigo-400 ring-1 ring-indigo-500/30'
                            : 'bg-emerald-500/15 text-emerald-400 ring-1 ring-emerald-500/30'
                      )}
                    >
                      {call.tableNumberLabel ?? '—'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="truncate text-sm font-semibold text-white/80">
                          Mesa {call.tableNumberLabel}
                        </p>
                        <span
                          className={clsx(
                            'inline-flex shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
                            call.status === 'Open' && call.escalatedAt
                              ? 'bg-rose-500/20 text-rose-300'
                              : call.status === 'Open'
                                ? 'bg-indigo-500/20 text-indigo-300'
                                : 'bg-emerald-500/20 text-emerald-300'
                          )}
                        >
                          {call.status === 'Open' && call.escalatedAt ? 'Escalada' : call.status === 'Open' ? 'Aberta' : 'Atendendo'}
                        </span>
                      </div>
                      {call.tableArea && (
                        <p className="mt-0.5 truncate text-xs text-white/30">{call.tableArea}</p>
                      )}
                      <p className="mt-1 text-xs text-white/25">
                        {formatTime(call.openedAt)} · {formatElapsed(call.openedAt, nowTick)}
                      </p>
                    </div>
                    {call.recallCount > 0 && (
                      <span className="shrink-0 text-xs font-medium text-amber-400/70">
                        {call.recallCount}×
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </aside>
      </main>

      {/* ─── bottom bar ─── */}
      <footer className="flex shrink-0 items-center justify-between border-t border-white/10 bg-gray-900/80 px-6 py-2.5 backdrop-blur-sm">
        <div className="flex items-center gap-3">
          <span className="text-xs font-medium uppercase tracking-wider text-white/30">
            Orion WaiterCall
          </span>
          {companyName && (
            <>
              <span className="h-1 w-1 rounded-full bg-white/15" />
              <span className="text-xs text-white/25">{companyName}</span>
            </>
          )}
          {restaurantName && (
            <>
              <span className="h-1 w-1 rounded-full bg-white/15" />
              <span className="text-xs text-white/25">{restaurantName}</span>
            </>
          )}
        </div>
        <div className="flex items-center gap-4">
          <span className="text-xs capitalize text-white/25">{currentDate}</span>
          <span className="text-sm font-semibold tabular-nums text-white/50">{currentTime}</span>
        </div>
      </footer>

      {/* ─── custom animations ─── */}
      <style>{`
        @keyframes pulse-subtle {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.85; }
        }
        .animate-pulse-subtle {
          animation: pulse-subtle 2s ease-in-out infinite;
        }
        @keyframes fade-in {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-in {
          animation: fade-in 0.5s ease-out;
        }
      `}</style>
    </div>
  );
}
